import {PhoneHQInput, PhoneHQGlyph, PhoneHQButton, PhoneHQDropdown} from 'ch3-ui-lib';
import {AVAILABLE_ENS_CHANNELS, EnsChannel, EnsLevelInputDto} from "../../../store/features/ens/types";
import {
    ENS_SMS_SENDER_NOT_CONFIGURED_MESSAGE,
    normalizeEscalationLevels,
    requiresVoicePrompt,
} from "./utils";

const CHANNEL_LABEL: Record<EnsChannel, string> = {
    PUSH: 'Push',
    INTERNAL_CALL: 'Internal call',
    SMS: 'SMS',
    PSTN: 'Phone call',
    WHATSAPP: 'WhatsApp',
};

const MAX_LEVELS = 10;
const DEFAULT_NEW_LEVEL_DELAY = 60;
const SECONDARY_BUTTON_CLASS =
    '!h-9 !rounded-lg !border !border-line !px-4 !py-0 !text-ink hover:!border-brand-focus hover:!text-brand-ink focus:!border-brand focus:!text-brand-ink disabled:!border-line disabled:!text-ink-disabled';

interface Props {
    levels: EnsLevelInputDto[];
    onChange: (levels: EnsLevelInputDto[]) => void;
    voicePromptSet: boolean;
    smsSenderConfigured: boolean;
    enforceVoicePrompt?: boolean;
}

const updateLevel = (
    levels: EnsLevelInputDto[],
    index: number,
    patch: Partial<EnsLevelInputDto>,
) => levels.map((l, idx) => (idx === index ? {...l, ...patch} : l));

const renderChannelDropdownTrigger = (label: string) => (
    <button
        type="button"
        className="flex h-9 w-[160px] items-center justify-between rounded-lg border border-line bg-surface px-3 text-sm font-medium text-ink transition-colors hover:border-brand-focus focus:border-brand focus:outline-none"
    >
        <span className="truncate">{label}</span>
        <PhoneHQGlyph name="keyboard_arrow_down" size={20} className="text-ink-muted"/>
    </button>
);

export const Step3Escalation = ({levels, onChange, voicePromptSet, smsSenderConfigured, enforceVoicePrompt = true}: Props) => {
    const voicePromptMissing = enforceVoicePrompt && requiresVoicePrompt(levels) && !voicePromptSet;
    const smsSenderMissing = !smsSenderConfigured && levels.some((level) => level.channel === 'SMS');
    const addLevel = () => {
        if (levels.length >= MAX_LEVELS) return;
        onChange(normalizeEscalationLevels([
            ...levels,
            {
                sequenceNo: levels.length + 1,
                channel: 'PSTN',
                delaySecondsFromPrevious: DEFAULT_NEW_LEVEL_DELAY,
            },
        ]));
    };

    const removeLevel = (index: number) => {
        if (levels.length <= 1) return;
        onChange(normalizeEscalationLevels(levels.filter((_, idx) => idx !== index)));
    };

    const lastLevel = levels[levels.length - 1];
    const lastLevelLabel = lastLevel ? CHANNEL_LABEL[lastLevel.channel].toLowerCase() : 'level';

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h3 className="text-sm font-semibold text-ink">Escalation plan</h3>
                    <p className="mt-1 text-sm leading-5 text-ink-muted">
                        Levels run sequentially. Each delay is added on top of the previous level.
                    </p>
                </div>
                <PhoneHQButton
                    phq-id="ensWizard-addLevelBtn"
                    size="sm"
                    variant="secondary"
                    onClick={addLevel}
                    disabled={levels.length >= MAX_LEVELS}
                    className={SECONDARY_BUTTON_CLASS}
                >
                    <PhoneHQGlyph name="add" size={20}/>
                    Add level
                </PhoneHQButton>
            </div>

            <div className="flex flex-col gap-2.5">
                {levels.map((level, index) => {
                    const isFirst = index === 0;
                    return (
                        <div
                            key={index}
                            className="flex items-center justify-between gap-3 rounded-lg border border-line bg-surface px-4 py-3"
                        >
                            <div className="flex min-w-0 items-center gap-3">
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-sm font-semibold text-ink">
                                    L{level.sequenceNo}
                                </div>
                                <PhoneHQDropdown
                                    align="start"
                                    sideOffset={4}
                                    trigger={renderChannelDropdownTrigger(CHANNEL_LABEL[level.channel])}
                                >
                                    {AVAILABLE_ENS_CHANNELS.map((ch) => {
                                        const disabled = ch === 'SMS' && !smsSenderConfigured;
                                        return (
                                            <PhoneHQDropdown.Item
                                                key={ch}
                                                disabled={disabled}
                                                onSelect={() =>
                                                    onChange(normalizeEscalationLevels(updateLevel(levels, index, {channel: ch})))
                                                }
                                            >
                                                <div className="flex w-[180px] items-center justify-between gap-3">
                                                    <div className="flex flex-col">
                                                        <span>{CHANNEL_LABEL[ch]}</span>
                                                        {disabled && (
                                                            <span className="text-xs text-zinc-400">
                                                                {ENS_SMS_SENDER_NOT_CONFIGURED_MESSAGE}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {ch === level.channel && (
                                                        <PhoneHQGlyph name="check" size={16}/>
                                                    )}
                                                </div>
                                            </PhoneHQDropdown.Item>
                                        );
                                    })}
                                </PhoneHQDropdown>
                            </div>
                            <div className="flex items-center gap-2">
                                {isFirst ? (
                                    <span className="inline-flex h-8 items-center rounded-md bg-zinc-100 px-3 text-xs font-semibold uppercase text-ink-muted">
                                        Immediate
                                    </span>
                                ) : (
                                    <div className="flex items-center gap-1">
                                        <PhoneHQInput
                                            phq-id={`ensWizard-delayL${level.sequenceNo}`}
                                            type="number"
                                            containerClassName="w-20"
                                            className="text-right"
                                            value={String(Math.round(level.delaySecondsFromPrevious / 60))}
                                            onChange={({target: {value: v}}) => {
                                                const minutes = Math.max(1, Number(v) || 1);
                                                onChange(updateLevel(levels, index, {delaySecondsFromPrevious: minutes * 60}));
                                            }}
                                        />
                                        <span className="text-xs text-ink-muted">min</span>
                                    </div>
                                )}
                                <button
                                    type="button"
                                    aria-label={`Remove L${level.sequenceNo}`}
                                    onClick={() => removeLevel(index)}
                                    disabled={levels.length <= 1}
                                    className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-muted transition-colors hover:bg-danger-tint hover:text-danger-ink disabled:cursor-not-allowed disabled:opacity-30"
                                >
                                    <PhoneHQGlyph name="delete" size={20}/>
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {voicePromptMissing && (
                <div className="flex items-start gap-2 rounded-lg border border-warning-line bg-warning-tint px-4 py-3 text-sm text-warning-ink">
                    <PhoneHQGlyph name="info" size={16} className="mt-0.5"/>
                    <span>
                        Voice prompt required for phone call and internal call escalation. Go back to <span className="font-semibold">Step 2</span> to generate it.
                    </span>
                </div>
            )}

            {smsSenderMissing && (
                <div className="flex items-start gap-2 rounded-lg border border-warning-line bg-warning-tint px-4 py-3 text-sm text-warning-ink">
                    <PhoneHQGlyph name="info" size={16} className="mt-0.5"/>
                    <span>{ENS_SMS_SENDER_NOT_CONFIGURED_MESSAGE}</span>
                </div>
            )}

            <div className="rounded-lg border border-brand-tint bg-brand-tint-subtle px-4 py-3 text-sm text-brand-ink">
                L{levels.length} ({lastLevelLabel}) is the final escalation. If no confirmation is received after
                L{levels.length}, the alert expires and is logged as Failed.
            </div>
        </div>
    );
};
